import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile as firebaseUpdateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  User,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import type { UserProfile } from '@/types';

/**
 * ログイン
 */
export const signIn = async (email: string, password: string): Promise<User> => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};
/**
 * Googleログイン
 */
export const signInWithGoogle = async (): Promise<User> => {
  const provider = new GoogleAuthProvider();
  const userCredential = await signInWithPopup(auth, provider);
  const user = userCredential.user;

  // Firestoreにユーザープロフィールがない場合は作成
  const userProfile = await getUserProfile(user.uid);
  if (!userProfile) {
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || 'User',
      photoURL: user.photoURL || null,
      plan: 'Free',
      planStartDate: null,
      planEndDate: null,
      invitationCode: '',
      autoRenew: false,
      usedInvitationCode: false,
      stripeCustomerId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  return user;
};


/**
 * サインアップ
 */
export const signUp = async (
  email: string,
  password: string,
  displayName: string
): Promise<User> => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  // Authenticationのプロフィール更新
  await firebaseUpdateProfile(user, { displayName });

  // Firestoreにユーザープロフィールを作成
  await setDoc(doc(db, 'users', user.uid), {
    uid: user.uid,
    email: user.email,
    displayName,
    photoURL: user.photoURL || null,
    plan: 'Free',
    planStartDate: null,
    planEndDate: null,
    invitationCode: '', // Cloud Functionsで自動生成
    autoRenew: false,
    usedInvitationCode: false,
    stripeCustomerId: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return user;
};

/**
 * ログアウト
 */
export const signOut = async (): Promise<void> => {
  await firebaseSignOut(auth);
};

/**
 * ユーザープロフィールを取得
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const data = docSnap.data();
    return {
      uid, // 確実に uid を含める
      ...data,
    } as UserProfile;
  }
  return null;
};

/**
 * ユーザープロフィールを更新
 */
export const updateUserProfile = async (
  uid: string,
  data: Partial<UserProfile>
): Promise<void> => {
  const docRef = doc(db, 'users', uid);
  await updateDoc(docRef, {
    ...data,
    updatedAt: serverTimestamp(),
  });
};
